import type { Mountain, Project } from '@/types';

/** 「可访问项目」级联选择器：山头 → 项目 */
export interface UserProjectCascaderOption {
  label: string;
  value: string;
  children: { label: string; value: string }[];
}

/** 未归属任何山头的项目归到这个虚拟分组 */
export const UNGROUPED_MOUNTAIN_VALUE = '__mtn__未分配';

/** 父级节点用山头 id 而非山头名：改名或 store 陈旧都不会让回显路径对不上选项 */
const mountainValue = (mountainId: number) => `__mtn__${mountainId}`;

export function buildUserProjectCascaderOptions(
  projects: Project[],
  mountains: Mountain[],
): UserProjectCascaderOption[] {
  const childrenByMountainId = new Map<number, { label: string; value: string }[]>();
  const ungrouped: { label: string; value: string }[] = [];

  projects.forEach((p) => {
    if (p.mountain) {
      const children = childrenByMountainId.get(p.mountain.id) ?? [];
      children.push({ label: p.name, value: String(p.id) });
      childrenByMountainId.set(p.mountain.id, children);
    } else {
      ungrouped.push({ label: p.name, value: String(p.id) });
    }
  });

  const result: UserProjectCascaderOption[] = [];
  // 只输出有项目的山头：没有项目的山头无法分配，列出来也选不了
  mountains.forEach((m) => {
    const children = childrenByMountainId.get(m.id);
    if (children) result.push({ label: m.name, value: mountainValue(m.id), children });
  });
  if (ungrouped.length > 0) {
    result.push({ label: '未分配', value: UNGROUPED_MOUNTAIN_VALUE, children: ungrouped });
  }
  return result;
}

/** 已授权的项目 id 列表 → Cascader 的 value（叶子路径） */
export function projectIdsToCascaderValue(ids: number[], projects: Project[]): string[][] {
  return ids
    .map((id) => {
      const p = projects.find((proj) => proj.id === id);
      if (!p) return null;
      return [p.mountain ? mountainValue(p.mountain.id) : UNGROUPED_MOUNTAIN_VALUE, String(id)];
    })
    .filter((v): v is string[] => v !== null);
}

/**
 * Cascader 的 onChange 路径 → 项目 id 列表。
 *
 * 只认叶子路径（末段是项目 id）。父级路径末段是 `__mtn__*`，parseInt 得 NaN 会被丢掉，
 * 所以 Cascader 必须配 `showCheckedStrategy={Cascader.SHOW_CHILD}`——默认的 SHOW_PARENT
 * 会在子节点被勾满时折叠成父路径，导致勾选静默失效。
 */
export function cascaderValueToProjectIds(paths: string[][] | undefined): number[] {
  return (paths || [])
    .map((path) => parseInt(path[path.length - 1], 10))
    .filter((n) => !Number.isNaN(n));
}

/**
 * 用户的底软供应商由他勾选的可访问项目推导，不再单独维护。
 * 去重后按供应商 id 排序，保证渲染顺序稳定。
 */
export function deriveSupplierNames(projectIds: number[], projects: Project[]): string[] {
  const idSet = new Set(projectIds);
  const found = new Map<number, string>();
  projects.forEach((p) => {
    if (!idSet.has(p.id)) return;
    (p.software_suppliers ?? []).forEach((s) => found.set(s.id, s.name));
  });
  return [...found.entries()].sort((a, b) => a[0] - b[0]).map(([, name]) => name);
}
